import { Injectable, NgZone, inject } from '@angular/core';
import { Globals } from '../../globals';
import { AppUserAuth } from '../../interfaces/login.interfaces';
import { LogPublisher } from './log-publishers';
import { LogPublishersService } from './log-publishers.service';

// ****************************************************
// Log Level Enumeration
// ****************************************************
export enum LogLevel {
  All = 0,
  Debug = 1,
  Info = 2,
  Warn = 3,
  Error = 4,
  Fatal = 5,
  Off = 6,
}

// ****************************************************
// Log Entry Class
// ****************************************************
export class LogEntry {
  // Public Properties
  entryDate: Date = new Date();
  message = '';
  level: LogLevel = LogLevel.Debug;
  extraInfo: any[] = [];
  logWithDate = true;

  // **************
  // Public Methods
  // **************
  buildLogString(): string {
    let value = '';

    if (this.logWithDate) {
      value = new Date() + ' - ';
    }
    value += 'Type: ' + LogLevel[this.level];
    value += ' - Message: ' + this.message;
    if (this.extraInfo.length) {
      value += ' - Extra Info: ' + this.formatParams(this.extraInfo);
    }

    return value;
  }

  // ***************
  // Private Methods
  // ***************
  private formatParams(params: any[]): string {
    let ret: string = params.join(',');

    // Is there at least one object in the array?
    if (params.some((p) => typeof p === 'object')) {
      ret = '';
      // Build comma-delimited string
      for (const item of params) {
        ret += JSON.stringify(item) + ',';
      }
    }

    return ret;
  }
}

// ****************************************************
// Log Service Class
// ****************************************************
@Injectable()
export class LogService {
  private readonly publishersService = inject(LogPublishersService);
  private readonly globals = inject(Globals);
  private readonly ngZone = inject(NgZone);

  constructor() {
    // Set publishers
    this.publishers = this.publishersService.publishers;
  }

  // Public Properties
  publishers: LogPublisher[];
  level: LogLevel = LogLevel.Off;
  logWithDate = true;

  // *************************
  // Public methods
  // *************************
  debug(msg: string, ...optionalParams: any[]) {
    this.writeToLog(msg, LogLevel.Debug, optionalParams);
  }

  info(msg: string, ...optionalParams: any[]) {
    this.writeToLog(msg, LogLevel.Info, optionalParams);
  }

  warn(msg: string, ...optionalParams: any[]) {
    this.writeToLog(msg, LogLevel.Warn, optionalParams);
  }

  error(msg: string, ...optionalParams: any[]) {
    this.writeToLog(msg, LogLevel.Error, optionalParams);
  }

  fatal(msg: string, ...optionalParams: any[]) {
    this.writeToLog(msg, LogLevel.Fatal, optionalParams);
  }

  log(msg: string, ...optionalParams: any[]) {
    this.writeToLog(msg, LogLevel.All, optionalParams);
  }

  clear(): void {
    for (const logger of this.publishers) {
      logger.clear().subscribe();
    }
  }

  // *************************
  // Private methods
  // *************************
  private shouldLog(level: LogLevel): boolean {
    let ret = false;
    const currentUser = this.getCurrentUserFromLocalStorage();
    if (
      currentUser !== null &&
      currentUser !== undefined &&
      currentUser.hasOwnProperty('appLogLevel')
    ) {
      this.level = currentUser.appLogLevel!;
    } else {
      this.level = LogLevel.Off;
    }

    if (
      (level >= this.level && level !== LogLevel.Off) ||
      this.level === LogLevel.All
    ) {
      ret = true;
    }

    return ret;
  }

  private getCurrentUserFromLocalStorage(): AppUserAuth | null {
    const localStorageTokenName =
      localStorage.getItem(this.globals.localStorageTokenName) || null;
    return localStorageTokenName ? JSON.parse(localStorageTokenName) : null;
  }

  private writeToLog(msg: string, level: LogLevel, params: any[]) {
    this.ngZone.runOutsideAngular(() => {
      if (this.shouldLog(level)) {
        // Declare variables
        const entry: LogEntry = new LogEntry();

        // Build Log Entry
        entry.message = msg;
        entry.level = level;
        entry.extraInfo = params;
        entry.logWithDate = this.logWithDate;

        for (const logger of this.publishers) {
          try {
            logger.log(entry).subscribe();
          } catch (error: any) {
            logger.log(error.message).subscribe();
          }
        }
      }
    });
  }
}
